import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Result, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { translateGraphQLError } from '../../../../utils/graphqlUtils';
import { formatNumber } from '../batchPushUtils';
import {
    GET_BATCH_PUSH_RESULT,
    GetBatchPushResultResponse,
    GetBatchPushResultVariables,
    PUSH_STATUS,
} from '../graphql/batchPush';

/** 轮询间隔（毫秒） */
export const RESULT_POLL_INTERVAL_MS = 2000;
/** 轮询上限（毫秒）：调度侧推送中超过 5 分钟会被置为失败（执行中断），这里多留 1 分钟 */
export const RESULT_POLL_MAX_MS = 6 * 60 * 1000;

interface BatchPushResultPanelProps {
    warehouseId: number;
    pushRecordId: number;
    retrying: boolean;
    onViewHistory: () => void;
    onContinue: () => void;
    onRetry: () => void;
    onBack: () => void;
}

/**
 * 推送结果页：推送中（轮询 getBatchPushResult）/ 成功 / 失败 / 轮询超时。
 * 一次推送要么全部成功、要么全部失败；失败后原已选数据仍在待推送，可「重新推送」或「返回修改」。
 */
const BatchPushResultPanel: React.FC<BatchPushResultPanelProps> = ({
    warehouseId,
    pushRecordId,
    retrying,
    onViewHistory,
    onContinue,
    onRetry,
    onBack,
}) => {
    const { t } = useTranslation();
    const [timedOut, setTimedOut] = useState(false);
    const { data, error, stopPolling, startPolling } = useQuery<GetBatchPushResultResponse, GetBatchPushResultVariables>(
        GET_BATCH_PUSH_RESULT,
        {
            variables: { warehouseId, pushRecordId },
            fetchPolicy: 'network-only',
            pollInterval: RESULT_POLL_INTERVAL_MS,
        },
    );
    const status = data?.result?.pushStatus;
    const done = status === PUSH_STATUS.SUCCESS || status === PUSH_STATUS.FAILED;

    // 每次进入（含重新推送换新记录）开始轮询，并用定时器兜底最长轮询时间：
    // 推送中每轮返回相同结果时 Apollo 不会触发重渲染，不能靠渲染时判断超时
    useEffect(() => {
        setTimedOut(false);
        startPolling(RESULT_POLL_INTERVAL_MS);
        const timer = setTimeout(() => {
            stopPolling();
            setTimedOut(true);
        }, RESULT_POLL_MAX_MS);
        return () => {
            clearTimeout(timer);
            stopPolling();
        };
    }, [pushRecordId, startPolling, stopPolling]);

    useEffect(() => {
        if (done) {
            stopPolling();
        }
    }, [done, stopPolling]);

    const result = data?.result;
    if (status === PUSH_STATUS.SUCCESS) {
        return (
            <Result
                status="success"
                title={t('wms.batchPush.result.success')}
                subTitle={
                    <>
                        <div>
                            {t('wms.batchPush.result.successDesc', {
                                parcelCount: formatNumber(result?.parcelCount),
                                areaCount: formatNumber(result?.areaCount),
                            })}
                        </div>
                        {/* 确认弹框到执行之间有包裹出池 / 新入池时，展示已选与实际推送的差异（Q36） */}
                        {result?.selectedParcelCount != null && result.selectedParcelCount !== result.parcelCount && (
                            <div className="text-orange-500">
                                {t('wms.batchPush.result.selectedVsPushed', {
                                    selected: formatNumber(result.selectedParcelCount),
                                    pushed: formatNumber(result.parcelCount),
                                })}
                            </div>
                        )}
                    </>
                }
                extra={[
                    <Button key="history" onClick={onViewHistory}>{t('wms.batchPush.result.viewHistory')}</Button>,
                    <Button key="continue" type="primary" onClick={onContinue}>{t('wms.batchPush.result.continue')}</Button>,
                ]}
            />
        );
    }
    if (status === PUSH_STATUS.FAILED) {
        return (
            <Result
                status="error"
                title={t('wms.batchPush.result.failed')}
                subTitle={t('wms.batchPush.result.failReason', {
                    reason: result?.failReason || t('wms.batchPush.result.unknownReason'),
                })}
                extra={[
                    <Button key="back" onClick={onBack} disabled={retrying}>{t('wms.batchPush.result.back')}</Button>,
                    <Button key="retry" type="primary" loading={retrying} onClick={onRetry}>{t('wms.batchPush.result.retry')}</Button>,
                ]}
            />
        );
    }
    if (error && !data) {
        // 查询失败（如推送记录不存在、网络异常）：展示真实原因，推送本身可能仍在执行
        return (
            <Result
                status="warning"
                title={translateGraphQLError(error, 'wms.batchPush.messages.operationFailed')}
                subTitle={t('wms.batchPush.result.queryFailedHint')}
                extra={[
                    <Button key="back" onClick={onBack}>{t('wms.batchPush.result.back')}</Button>,
                    <Button key="history" type="primary" onClick={onViewHistory}>{t('wms.batchPush.result.viewHistory')}</Button>,
                ]}
            />
        );
    }
    if (timedOut) {
        return (
            <Result
                status="warning"
                title={t('wms.batchPush.result.timeout')}
                extra={[
                    <Button key="history" type="primary" onClick={onViewHistory}>{t('wms.batchPush.result.viewHistory')}</Button>,
                ]}
            />
        );
    }
    return (
        <div className="py-16 text-center">
            <Spin size="large" />
            <div className="mt-4 text-base">{t('wms.batchPush.result.pushing')}</div>
        </div>
    );
};

export default BatchPushResultPanel;
